import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import { Drawer } from 'antd'
import {
	LayoutDashboard,
	LogOut,
	Menu,
	ReceiptText,
	Sparkles,
} from 'lucide-react'
import { DEMO_DASHBOARD, DEMO_INVOICES } from '../constants'
import { CASHBACK_API_ROOT } from '../../../config'

const NAV_ITEMS = [
	{
		path: DEMO_DASHBOARD,
		label: 'Dashboard',
		hint: 'Statistika',
		icon: LayoutDashboard,
	},
	{
		path: DEMO_INVOICES,
		label: 'Hujjatlar',
		hint: 'A/R Invoices',
		icon: ReceiptText,
	},
]

// «Yangi hujjat» sidebarda yo'q — u faqat «Hujjatlar» ichidan ochiladi,
// shuning uchun /demo/invoices/new da ham «Hujjatlar» aktiv turadi.
const isActivePath = (pathname, path) => {
	if (path === DEMO_DASHBOARD)
		return pathname === DEMO_DASHBOARD || pathname === `${DEMO_DASHBOARD}/`
	return pathname.startsWith(path)
}

const apiHost = (() => {
	try {
		return new URL(CASHBACK_API_ROOT).host
	} catch {
		return CASHBACK_API_ROOT
	}
})()

const SidebarContent = ({ pathname, onNavigate }) => {
	const { getMe } = useSelector((state) => state.main)
	const fullName = `${get(getMe, 'FirstName', '')} ${get(getMe, 'LastName', '')}`.trim()

	return (
		<div className="flex h-full flex-col">
			<div className="flex items-center gap-3 px-5 py-5">
				<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-sm shadow-indigo-200">
					<Sparkles size={18} />
				</div>
				<div className="min-w-0">
					<p className="truncate text-[15px] font-semibold text-slate-900">
						A/R Invoices
					</p>
					<p className="truncate text-[11px] uppercase tracking-wide text-slate-400">
						iComfort · demo
					</p>
				</div>
			</div>

			<nav className="flex flex-1 flex-col gap-1 px-3">
				{NAV_ITEMS.map(({ path, label, hint, icon: Icon }) => {
					const active = isActivePath(pathname, path)
					return (
						<button
							key={path}
							type="button"
							onClick={() => onNavigate(path)}
							className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
								active
									? 'bg-indigo-50 text-indigo-700'
									: 'text-slate-600 hover:bg-slate-50'
							}`}
						>
							<span
								className={`flex h-8 w-8 items-center justify-center rounded-lg ${
									active
										? 'bg-white text-indigo-600 shadow-sm'
										: 'bg-slate-100 text-slate-500 group-hover:text-slate-700'
								}`}
							>
								<Icon size={16} />
							</span>
							<span className="min-w-0">
								<span className="block truncate text-sm font-medium">
									{label}
								</span>
								<span className="block truncate text-[11px] text-slate-400">
									{hint}
								</span>
							</span>
						</button>
					)
				})}
			</nav>

			<div className="border-t border-slate-100 p-3">
				{fullName && (
					<div className="mb-2 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
						<div className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100 text-[12px] font-semibold text-indigo-600">
							{fullName[0]}
						</div>
						<span className="min-w-0 truncate text-[13px] text-slate-600">
							{fullName}
						</span>
					</div>
				)}
				<button
					type="button"
					onClick={() => onNavigate('/root')}
					className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-[13px] text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
				>
					<LogOut size={15} /> Tizimga qaytish
				</button>
				<p
					className="mt-2 truncate px-3 font-mono text-[10px] text-slate-300"
					title={CASHBACK_API_ROOT}
				>
					{apiHost}
				</p>
			</div>
		</div>
	)
}

const DemoShell = ({ title, subtitle, actions, children }) => {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const [mobileOpen, setMobileOpen] = useState(false)

	const handleNavigate = (path) => {
		setMobileOpen(false)
		navigate(path)
	}

	return (
		<div className="flex min-h-screen bg-[#F5F7FB] text-slate-800">
			<aside className="sticky top-0 hidden h-screen w-[248px] shrink-0 border-r border-slate-200 bg-white lg:block">
				<SidebarContent pathname={pathname} onNavigate={handleNavigate} />
			</aside>

			<Drawer
				open={mobileOpen}
				placement="left"
				width={260}
				closable={false}
				styles={{ body: { padding: 0 } }}
				onClose={() => setMobileOpen(false)}
			>
				<SidebarContent pathname={pathname} onNavigate={handleNavigate} />
			</Drawer>

			<div className="flex min-w-0 flex-1 flex-col">
				<header className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/85 backdrop-blur">
					<div className="flex min-h-16 flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
						<button
							type="button"
							onClick={() => setMobileOpen(true)}
							className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 lg:hidden"
						>
							<Menu size={17} />
						</button>
						<div className="min-w-0 flex-1">
							<h1 className="truncate text-[17px] font-semibold text-slate-900">
								{title}
							</h1>
							{subtitle && (
								<p className="truncate text-[12px] text-slate-400">{subtitle}</p>
							)}
						</div>
						{actions}
					</div>
				</header>

				<main className="w-full px-4 py-5 sm:px-6">{children}</main>
			</div>
		</div>
	)
}

export default DemoShell
